/**
 * A rule box's rules and price as a merchant types them, shared by the embedded admin's builder
 * (apps/shopify) and the dashboard's rule editor, so both read a form the same way. Shopify shops
 * are USD, so prices here are dollars, never the tenant's storefront currency.
 */
import {
  RULE_BOX_DEFAULT_MARGIN_BPS,
  RULE_BOX_LOWEST_MIN_RETURN_BPS,
  RULE_BOX_MAX_ITEMS,
  RULE_BOX_MAX_MARGIN_BPS,
  RULE_BOX_MIN_ITEMS,
  RULE_BOX_MIN_MARGIN_BPS,
  RULE_BOX_MIN_RETURN_BPS,
  RULE_BOX_NEW_MIN_RETURN_BPS,
  type RuleBoxCategoryFilter,
  type RuleBoxChoice,
  type RuleBoxConfig,
  type RuleBoxFacets,
  type RuleBoxOptionFilter,
  type RuleBoxPriceMode,
  type RuleBoxRules,
} from "./shopifyRuleBox.js";

/** Cents as dollars, for a USD shop. */
export function dollars(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/**
 * A dollars field ("12.50", "$1,200") in whole cents, or null when it is not a price above zero
 * with at most two decimal places.
 */
export function parseDollars(value: string): number | null {
  const cleaned = value.trim().replace(/^\$/, "").replaceAll(",", "");
  if (!/^\d+(\.\d{0,2})?$|^\.\d{1,2}$/.test(cleaned)) return null;
  const cents = Math.round(Number(cleaned) * 100);
  return Number.isSafeInteger(cents) && cents > 0 ? cents : null;
}

/** Cents as a dollars field's text: "50" for whole dollars, "49.99" otherwise. */
export function dollarsField(cents: number): string {
  return cents % 100 === 0 ? String(cents / 100) : (cents / 100).toFixed(2);
}

/**
 * The builder's rules and price as the merchant types them: lists of chosen tags, vendors,
 * product types, collections and locations, the option and category attribute filters, and text
 * for every number (dollars and a margin percentage), so a half-typed field stays as typed.
 * `parseRuleBoxForm` turns it into the config the server takes.
 */
export interface RuleBoxForm {
  includeTags: string[];
  excludeTags: string[];
  vendors: string[];
  productTypes: string[];
  /** Collection GIDs. */
  collections: string[];
  /** Location GIDs. */
  locations: string[];
  /** One per option the merchant added; a group with no values yet is dropped by the parse. */
  options: RuleBoxOptionFilter[];
  /** One per category attribute the merchant added; empty groups are dropped by the parse. */
  categoryAttributes: RuleBoxCategoryFilter[];
  /** Dollars; "" = no lower bound. */
  minPrice: string;
  /** Dollars; "" = no upper bound. */
  maxPrice: string;
  /** Percent of the price the merchant keeps on average, e.g. "20". */
  marginPercent: string;
  priceMode: RuleBoxPriceMode;
  /** Dollars; read only with the `fixed` price mode. */
  fixedPrice: string;
  /** A whole number; "" = no limit of the merchant's own. */
  minItems: string;
  /** A whole number; "" = no limit of the merchant's own. */
  maxItems: string;
  /**
   * Percent of the box price every item is worth at least, e.g. "50"; "" = the platform's
   * `RULE_BOX_MIN_RETURN_BPS`, what a box saved before this setting holds.
   */
  minReturnPercent: string;
}

/** The typed fields a parse can refuse, each with its own message. */
export type RuleBoxFormField =
  | "minPrice"
  | "maxPrice"
  | "marginPercent"
  | "fixedPrice"
  | "minItems"
  | "maxItems"
  | "minReturnPercent";

export type ParsedRuleBoxForm =
  | { ok: true; config: RuleBoxConfig }
  | { ok: false; errors: Partial<Record<RuleBoxFormField, string>> };

/** The lowest and highest margin a merchant may pick, as percentages. */
export const MIN_MARGIN_PERCENT = RULE_BOX_MIN_MARGIN_BPS / 100;
export const MAX_MARGIN_PERCENT = RULE_BOX_MAX_MARGIN_BPS / 100;

/** The lowest and highest minimum return a merchant may pick, as percentages. */
export const LOWEST_MIN_RETURN_PERCENT = RULE_BOX_LOWEST_MIN_RETURN_BPS / 100;
export const HIGHEST_MIN_RETURN_PERCENT = RULE_BOX_MIN_RETURN_BPS / 100;

/** The price modes in the merchant's words. */
export const PRICE_MODE_LABEL: Record<RuleBoxPriceMode, string> = {
  auto: "Estimated",
  fixed: "Fixed price",
};

/**
 * A new box's form: no rules, the default margin, an estimated price, and the minimum return that
 * lets most catalogs fill tiered odds (`RULE_BOX_NEW_MIN_RETURN_BPS`).
 */
export function emptyRuleBoxForm(): RuleBoxForm {
  return {
    includeTags: [],
    excludeTags: [],
    vendors: [],
    productTypes: [],
    collections: [],
    locations: [],
    options: [],
    categoryAttributes: [],
    minPrice: "",
    maxPrice: "",
    marginPercent: String(RULE_BOX_DEFAULT_MARGIN_BPS / 100),
    priceMode: "auto",
    fixedPrice: "",
    minItems: "",
    maxItems: "",
    minReturnPercent: String(RULE_BOX_NEW_MIN_RETURN_BPS / 100),
  };
}

/** A saved config as the form shows it, for an edit. */
export function ruleBoxFormFromConfig(config: RuleBoxConfig): RuleBoxForm {
  const { rules } = config;
  return {
    includeTags: [...rules.includeTags],
    excludeTags: [...rules.excludeTags],
    vendors: [...rules.vendors],
    productTypes: [...rules.productTypes],
    collections: [...rules.collections],
    locations: [...rules.locations],
    options: rules.options.map((o) => ({ name: o.name, values: [...o.values] })),
    categoryAttributes: rules.categoryAttributes.map((a) => ({
      key: a.key,
      values: [...a.values],
    })),
    minPrice: rules.minPriceCents == null ? "" : dollarsField(rules.minPriceCents),
    maxPrice: rules.maxPriceCents == null ? "" : dollarsField(rules.maxPriceCents),
    marginPercent: String(config.marginBps / 100),
    priceMode: config.priceMode,
    fixedPrice: config.fixedPriceCents == null ? "" : dollarsField(config.fixedPriceCents),
    minItems: config.minItems == null ? "" : String(config.minItems),
    maxItems: config.maxItems == null ? "" : String(config.maxItems),
    minReturnPercent: config.minReturnBps == null ? "" : String(config.minReturnBps / 100),
  };
}

/** A percentage field ("20", "12.5", "20%") in basis points, or null when it is not one. */
function parsePercentBps(value: string): number | null {
  const trimmed = value.trim().replace(/%$/, "");
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
  return Math.round(Number(trimmed) * 100);
}

/** A margin field ("20", "12.5") in basis points within the allowed range, or null. */
export function parseMarginPercent(value: string): number | null {
  const bps = parsePercentBps(value);
  return bps !== null && bps >= RULE_BOX_MIN_MARGIN_BPS && bps <= RULE_BOX_MAX_MARGIN_BPS
    ? bps
    : null;
}

/** A minimum return field ("50") in basis points within the allowed range, or null. */
export function parseMinReturnPercent(value: string): number | null {
  const bps = parsePercentBps(value);
  return bps !== null && bps >= RULE_BOX_LOWEST_MIN_RETURN_BPS && bps <= RULE_BOX_MIN_RETURN_BPS
    ? bps
    : null;
}

/**
 * An item count field ("25") as a whole number from `RULE_BOX_MIN_ITEMS` to `RULE_BOX_MAX_ITEMS`,
 * or null.
 */
export function parseItemCount(value: string): number | null {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const count = Number(trimmed);
  return count >= RULE_BOX_MIN_ITEMS && count <= RULE_BOX_MAX_ITEMS ? count : null;
}

/**
 * The form as the config `boxes.preview`, `create` and `update` take, or the message for every
 * field it cannot read. An empty price bound or item limit is no bound.
 */
export function parseRuleBoxForm(form: RuleBoxForm): ParsedRuleBoxForm {
  const errors: Partial<Record<RuleBoxFormField, string>> = {};
  const minPriceCents = form.minPrice.trim() === "" ? null : parseDollars(form.minPrice);
  const maxPriceCents = form.maxPrice.trim() === "" ? null : parseDollars(form.maxPrice);
  if (form.minPrice.trim() !== "" && minPriceCents === null) {
    errors.minPrice = "Enter a price, like 50 or 49.99.";
  }
  if (form.maxPrice.trim() !== "" && maxPriceCents === null) {
    errors.maxPrice = "Enter a price, like 800 or 799.99.";
  }
  if (minPriceCents !== null && maxPriceCents !== null && minPriceCents > maxPriceCents) {
    errors.maxPrice = "The highest price must be at least the lowest.";
  }
  const marginBps = parseMarginPercent(form.marginPercent);
  if (marginBps === null) {
    errors.marginPercent = `Enter a margin from ${MIN_MARGIN_PERCENT}% to ${MAX_MARGIN_PERCENT}%.`;
  }
  const fixedPriceCents = form.priceMode === "fixed" ? parseDollars(form.fixedPrice) : null;
  if (form.priceMode === "fixed" && (fixedPriceCents === null || fixedPriceCents < 100)) {
    errors.fixedPrice = "Enter a price of at least $1, like 75 or 74.99.";
  }
  const minItems = form.minItems.trim() === "" ? null : parseItemCount(form.minItems);
  const maxItems = form.maxItems.trim() === "" ? null : parseItemCount(form.maxItems);
  const countRange = `a whole number from ${RULE_BOX_MIN_ITEMS} to ${RULE_BOX_MAX_ITEMS}`;
  if (form.minItems.trim() !== "" && minItems === null) {
    errors.minItems = `Enter ${countRange}, like 25.`;
  }
  if (form.maxItems.trim() !== "" && maxItems === null) {
    errors.maxItems = `Enter ${countRange}, like 36.`;
  }
  if (minItems !== null && maxItems !== null && minItems > maxItems) {
    errors.maxItems = "The most items must be at least the fewest.";
  }
  const minReturnBps =
    form.minReturnPercent.trim() === "" ? null : parseMinReturnPercent(form.minReturnPercent);
  if (form.minReturnPercent.trim() !== "" && minReturnBps === null) {
    errors.minReturnPercent = `Enter a percentage from ${LOWEST_MIN_RETURN_PERCENT}% to ${HIGHEST_MIN_RETURN_PERCENT}%.`;
  }
  if (Object.keys(errors).length > 0 || marginBps === null) return { ok: false, errors };
  return {
    ok: true,
    config: {
      rules: {
        includeTags: form.includeTags,
        excludeTags: form.excludeTags,
        vendors: form.vendors,
        productTypes: form.productTypes,
        minPriceCents,
        maxPriceCents,
        collections: form.collections,
        locations: form.locations,
        options: form.options.filter((o) => o.values.length > 0),
        categoryAttributes: form.categoryAttributes.filter((a) => a.values.length > 0),
      },
      marginBps,
      priceMode: form.priceMode,
      fixedPriceCents,
      minItems,
      maxItems,
      minReturnBps,
    },
  };
}

/** `list` with `value` added once, ignoring case and surrounding spaces. */
export function addChoice(list: string[], value: string): string[] {
  const trimmed = value.trim();
  if (!trimmed) return list;
  const lower = trimmed.toLowerCase();
  return list.some((v) => v.toLowerCase() === lower) ? list : [...list, trimmed];
}

/**
 * The choices not yet chosen whose label contains `query`, ignoring case: those starting with it
 * first, then the rest, each group alphabetical. An empty query lists every unchosen choice.
 */
export function rankChoiceItems(
  choices: RuleBoxChoice[],
  chosen: string[],
  query: string,
): RuleBoxChoice[] {
  const taken = new Set(chosen.map((v) => v.toLowerCase()));
  const q = query.trim().toLowerCase();
  const byLabel = (a: RuleBoxChoice, b: RuleBoxChoice) =>
    a.label.localeCompare(b.label, undefined, { sensitivity: "base" });
  const open = choices.filter(
    (c) => !taken.has(c.id.toLowerCase()) && c.label.toLowerCase().includes(q),
  );
  const starts = open.filter((c) => c.label.toLowerCase().startsWith(q)).sort(byLabel);
  const contains = open.filter((c) => !c.label.toLowerCase().startsWith(q)).sort(byLabel);
  return [...starts, ...contains];
}

/** Plain values (tags, vendors, option values) as choices named by themselves. */
export function textChoices(values: string[]): RuleBoxChoice[] {
  return values.map((value) => ({ id: value, label: value }));
}

/** `rankChoiceItems` over plain values. */
export function rankChoices(options: string[], chosen: string[], query: string): string[] {
  return rankChoiceItems(textChoices(options), chosen, query).map((c) => c.id);
}

function sameName(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/**
 * `filters` with option `name`'s values set to `values`, added at the end when the option is new
 * (names compared ignoring case). Values may be empty while the merchant picks.
 */
export function setOptionFilter(
  filters: RuleBoxOptionFilter[],
  name: string,
  values: string[],
): RuleBoxOptionFilter[] {
  return filters.some((f) => sameName(f.name, name))
    ? filters.map((f) => (sameName(f.name, name) ? { ...f, values } : f))
    : [...filters, { name: name.trim(), values }];
}

/** `filters` without option `name`. */
export function removeOptionFilter(
  filters: RuleBoxOptionFilter[],
  name: string,
): RuleBoxOptionFilter[] {
  return filters.filter((f) => !sameName(f.name, name));
}

/** `filters` with category attribute `key`'s values set, added at the end when new. */
export function setCategoryFilter(
  filters: RuleBoxCategoryFilter[],
  key: string,
  values: string[],
): RuleBoxCategoryFilter[] {
  return filters.some((f) => f.key === key)
    ? filters.map((f) => (f.key === key ? { ...f, values } : f))
    : [...filters, { key, values }];
}

/** `filters` without category attribute `key`. */
export function removeCategoryFilter(
  filters: RuleBoxCategoryFilter[],
  key: string,
): RuleBoxCategoryFilter[] {
  return filters.filter((f) => f.key !== key);
}

/** A category metafield key as words, for an attribute the shop has no name for: "Color pattern". */
export function categoryKeyLabel(key: string): string {
  const words = key.replace(/[-_]+/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * The names a description gives the ids a rule holds, from the builder's facets. A rule naming
 * an id the facets lack (a deleted collection, a closed location) says so.
 */
export interface RuleBoxLabels {
  collections: Map<string, string>;
  locations: Map<string, string>;
  categoryAttributes: Map<string, string>;
  categoryValues: Map<string, string>;
}

/** The labels the facets carry. */
export function ruleBoxLabels(facets: RuleBoxFacets): RuleBoxLabels {
  return {
    collections: new Map(facets.collections.map((c) => [c.id, c.label])),
    locations: new Map(facets.locations.map((l) => [l.id, l.label])),
    categoryAttributes: new Map(facets.categoryAttributes.map((a) => [a.key, a.label])),
    categoryValues: new Map(
      facets.categoryAttributes.flatMap((a) => a.values.map((v) => [v.id, v.label] as const)),
    ),
  };
}

/** A choice list as its first value and how many more: "rolex +2". */
function firstAndCount(values: string[]): string {
  const [first, ...rest] = values;
  return rest.length ? `${first} +${rest.length}` : (first ?? "");
}

/** "1 collection", "3 collections". */
function count(n: number, noun: string): string {
  return `${n} ${noun}${n === 1 ? "" : "s"}`;
}

/** The rule's parts, each a list of values to join with "or". */
interface RuleParts {
  prefix: string;
  values: string[];
}

/**
 * Every part of a description, each value named through `labels` (or counted without them):
 * "Tagged …", "Not tagged …", "By …", "Type …", "In …", each category attribute, each option,
 * then the price, then "Stocked at …".
 */
function ruleParts(rules: RuleBoxRules, labels: RuleBoxLabels | null): RuleParts[] {
  const parts: RuleParts[] = [];
  const add = (prefix: string, values: string[]) => {
    if (values.length) parts.push({ prefix, values });
  };
  const named = (
    ids: string[],
    names: Map<string, string> | undefined,
    gone: string,
    noun: string,
  ): string[] => {
    if (!ids.length) return [];
    if (!names) return [count(ids.length, noun)];
    return ids.map((id) => names.get(id) ?? gone);
  };
  add("Tagged", rules.includeTags);
  add("Not tagged", rules.excludeTags);
  add("By", rules.vendors);
  add("Type", rules.productTypes);
  add("In", named(rules.collections, labels?.collections, "a deleted collection", "collection"));
  for (const attribute of rules.categoryAttributes) {
    const name = labels?.categoryAttributes.get(attribute.key) ?? categoryKeyLabel(attribute.key);
    add(name, named(attribute.values, labels?.categoryValues, "a removed value", "value"));
  }
  for (const option of rules.options) add(option.name, option.values);
  return parts;
}

function priceText(rules: RuleBoxRules): string | null {
  const { minPriceCents: min, maxPriceCents: max } = rules;
  if (min != null && max != null) return `${dollars(min)}–${dollars(max)}`;
  if (min != null) return `${dollars(min)} and up`;
  if (max != null) return `Up to ${dollars(max)}`;
  return null;
}

function describeParts(
  rules: RuleBoxRules,
  labels: RuleBoxLabels | null,
  values: (list: string[]) => string,
): string {
  const parts = ruleParts(rules, labels).map((p) => `${p.prefix} ${values(p.values)}`);
  const price = priceText(rules);
  if (price) parts.push(price);
  const locations = rules.locations.length
    ? labels
      ? rules.locations.map((id) => labels.locations.get(id) ?? "a closed location")
      : [count(rules.locations.length, "location")]
    : [];
  if (locations.length) parts.push(`Stocked at ${values(locations)}`);
  return parts.length ? parts.join(" · ") : "All your products";
}

/**
 * A box's rules short enough for one line of a table row, each list cut to its first value and a
 * count: "Tagged rolex +2 · By Gamba · $50.00–$800.00". `describeRules` writes every value.
 * Without `labels`, a collection, location or category value is counted rather than named.
 */
export function summarizeRules(rules: RuleBoxRules, labels: RuleBoxLabels | null = null): string {
  return describeParts(rules, labels, firstAndCount);
}

/**
 * A box's rules in one line: "Tagged rolex · Not tagged used · In Summer drop · Size M or L ·
 * $50.00–$800.00 · Stocked at Warehouse". Without `labels`, a collection, location or category
 * value is counted rather than named.
 */
export function describeRules(rules: RuleBoxRules, labels: RuleBoxLabels | null = null): string {
  return describeParts(rules, labels, (values) => values.join(" or "));
}
